import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { useAuth } from '@/auth/AuthContext'
import { organizationService } from '@/services/OrganizationService'
import { organizationKeys } from '@/hooks/queryKeys'
import type { OrganizationDto } from '@lib/dto/organization.dto'
import type { BillingOwnerListDto } from '@lib/dto/billing-owner.dto'
import type { FirstVenueInput, LegalAcceptanceInput } from '@lib/input/signup.input'

/**
 * The signed-in person's organization. Only asked for by the people who run one
 * — organization and venue admins, and a super admin in support mode; everyone
 * else has no organization of this kind and the query stays idle.
 */
export function useOrganization(): UseQueryResult<OrganizationDto> {
  const { user } = useAuth()
  const tenantId = user?.tenantId ?? null
  const runsOne =
    user?.role === 'ORGANIZATION_ADMIN' || user?.role === 'VENUE_ADMIN' || user?.role === 'SUPER_ADMIN'
  return useQuery({
    queryKey: organizationKeys.detail(tenantId ?? 'none'),
    queryFn: () => organizationService.get(),
    staleTime: 60_000,
    enabled: runsOne && tenantId !== null,
  })
}

/** Hand billing to another organization admin. The organization is refreshed from the answer. */
export function useTransferBillingOwner(): UseMutationResult<OrganizationDto, Error, { userId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => organizationService.transferBillingOwner(userId),
    onSuccess: (organization) => {
      queryClient.setQueryData(organizationKeys.detail(organization.id), organization)
    },
  })
}

/** Who the billing owner could hand billing to. Only asked for by the owner. */
export function useBillingOwnerCandidates(enabled: boolean): UseQueryResult<BillingOwnerListDto> {
  const { user } = useAuth()
  return useQuery({
    queryKey: [...organizationKeys.detail(user?.tenantId ?? 'none'), 'billing-owner-candidates'],
    queryFn: () => organizationService.billingOwnerCandidates(),
    enabled,
  })
}

/** Store the organization the API just returned — after the owner finishes a signup step. */
function useOrganizationStore(): (organization: OrganizationDto) => void {
  const queryClient = useQueryClient()
  return (organization) => queryClient.setQueryData(organizationKeys.detail(organization.id), organization)
}

/** The billing owner accepts the terms and privacy policy in force. */
export function useAcceptLegal(): UseMutationResult<OrganizationDto, Error, LegalAcceptanceInput> {
  const store = useOrganizationStore()
  return useMutation({ mutationFn: (input: LegalAcceptanceInput) => organizationService.acceptLegal(input), onSuccess: store })
}

/** The billing owner creates the first venue while finishing signup. */
export function useCreateFirstVenue(): UseMutationResult<OrganizationDto, Error, FirstVenueInput> {
  const store = useOrganizationStore()
  return useMutation({ mutationFn: (input: FirstVenueInput) => organizationService.createFirstVenue(input), onSuccess: store })
}
